import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import "@/App.css";
import axios from "axios";
import { Truck, Shield, CircleNotch, ArrowRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { ReceiptDetailModal } from "@/components/ReceiptDetailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
};

const COMPLETED_STATUS_STYLE = {
  received: { label: "Received", cls: "bg-[#ECFDF3] text-[#027A48]" },
  partial: { label: "Partially Received", cls: "bg-[#FEF3C7] text-[#92400E]" },
  failed: { label: "Receipt Failed", cls: "bg-[#FEE4E2] text-[#B42318]" },
};

// Sep 24 2026, ported UX (matching the sibling app's status-badge
// sequence exactly, user's explicit ask "match to the dot"): Pending
// Receipt (yellow) -> Moving to Warehouse (teal, once the Goods Receipt
// step is done and the warehouse-move job is actually running) ->
// Receipt Failed (red).
const rowStatus = (order) => {
  if (order.active_job?.kind === "inbound_receipt_relocate") {
    return { label: "Moving to Warehouse", cls: "bg-[#E1F4F6] text-[#0B6B74]" };
  }
  if (order.receipt_status === "failed") {
    return { label: "Receipt Failed", cls: "bg-[#FEE4E2] text-[#B42318]" };
  }
  return { label: "Pending Receipt", cls: "bg-[#FEF3C7] text-[#92400E]" };
};

// Inbound STO Receipt (Aug 28 2026) - lets any logged-in user receive a
// multi-line STO in one click instead of the SAP UI showing one row per
// line. See server.py's /api/inbound-receipts/* and
// inbound_receipt_service.py for the backend logic this drives.
export default function InboundReceiptsPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "completed"
  const [completedOrders, setCompletedOrders] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [modal, setModal] = useState(null); // { order, mode: "receive" | "view" }
  const [search, setSearch] = useState("");
  // User's ask: don't show items inline in the table - only reveal them
  // when a row is clicked (an expandable row, no SAP call triggered).
  const [expandedStoId, setExpandedStoId] = useState(null);
  const ordersRef = useRef(orders);
  ordersRef.current = orders;

  useEffect(() => {
    axios.get(`${API}/inbound-receipts/sites`).then(({ data }) => {
      setSites(data.sites || []);
      const defaultSite = (user?.bound_sites || []).find((s) => (data.sites || []).includes(s));
      if (defaultSite) setSiteFilter(defaultSite);
    }).catch(() => toast.error("Could not load receiving sites."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompletedOrders = useCallback(async () => {
    setCompletedLoading(true);
    try {
      const params = {};
      if (siteFilter !== "all") params.site_id = siteFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(`${API}/inbound-receipts/completed`, { params });
      setCompletedOrders(data.orders || []);
    } catch {
      toast.error("Could not load completed receipts.");
    } finally {
      setCompletedLoading(false);
    }
  }, [siteFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab === "completed") loadCompletedOrders();
  }, [activeTab, loadCompletedOrders]);

  const loadOrders = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inbound-receipts/pending`, {
        params: siteFilter === "all" ? {} : { site_id: siteFilter },
      });
      setOrders(data.orders || []);
    } catch {
      if (!silent) toast.error("Could not load pending receipts.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [siteFilter]);

  useEffect(() => { loadOrders(false); }, [loadOrders]);

  // Cross-tab visibility (user's explicit ask - "show a symbol on each
  // STO clearly so users know job is ON") - a lightweight background
  // reload, only while at least one row has a job running elsewhere,
  // so the "Moving to Warehouse" badge/disabled Receive button stay
  // accurate even without this tab's own modal open.
  useEffect(() => {
    const hasLive = ordersRef.current.some((o) => o.active_job);
    if (activeTab !== "pending" || !hasLive) return;
    const interval = setInterval(() => loadOrders(true), 3000);
    return () => clearInterval(interval);
  }, [activeTab, orders, loadOrders]);

  const openReceive = (order) => setModal({ order, mode: "receive" });
  const openView = (order) => setModal({ order, mode: "view" });
  const closeModal = () => setModal(null);
  const handleSettled = () => {
    loadOrders(true);
    if (activeTab === "completed") loadCompletedOrders();
  };

  // Oct 2026, user's explicit ask: a universal search bar on this page -
  // client-side filter (data for the active tab is already loaded)
  // matching across every identifier a user would actually search by:
  // STO ID, SAP Order #, ship route, ship-to location, and any SAP
  // delivery/notification number. Applies to whichever tab is active.
  const q = search.trim().toLowerCase();
  const matchesSearch = (haystackParts) => !q || haystackParts.filter(Boolean).join(" ").toLowerCase().includes(q);
  const filteredOrders = orders.filter((o) => matchesSearch([
    o.sto_id, o.sap_order_id, o.ship_from_site_id, o.ship_to_site_id, o.ship_to_location_name,
    ...(o.outbound_delivery_ids || []),
  ]));
  const filteredCompletedOrders = completedOrders.filter((o) => matchesSearch([
    o.sto_id, o.sap_order_id, o.ship_from_site_id, o.ship_to_site_id, o.ship_to_location_name,
    ...(o.outbound_delivery_display_ids || []),
  ]));

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="inbound-receipts-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Inbound Receipt</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0"><NavTabs /></div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#101828] flex items-center gap-2">
              <Truck size={28} weight="fill" className="text-[#0B6B74]" />
              Inbound Receipt
            </h1>
            <p className="text-sm text-[#667085] mt-1">
              Receive a Stock Transfer Order in one click — closes every SAP delivery line for it automatically.
            </p>
          </div>
          <div className="w-full sm:w-56">
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger data-testid="inbound-receipts-site-filter">
                <SelectValue placeholder="Receiving site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Receiving Sites</SelectItem>
                {sites.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-1 mb-5 bg-[#EEF2F1] p-1 rounded-lg w-fit" data-testid="inbound-receipts-tabs">
          <button
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === "pending" ? "bg-white text-[#0B6B74] shadow-sm" : "text-[#667085] hover:text-[#344054]"}`}
            onClick={() => setActiveTab("pending")}
            data-testid="inbound-receipts-tab-pending"
          >
            Pending
          </button>
          <button
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === "completed" ? "bg-white text-[#0B6B74] shadow-sm" : "text-[#667085] hover:text-[#344054]"}`}
            onClick={() => setActiveTab("completed")}
            data-testid="inbound-receipts-tab-completed"
          >
            Completed
          </button>
        </div>

        <div className="relative mb-5 max-w-md" data-testid="inbound-receipts-search-wrapper">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by STO ID, SAP Order #, route, or delivery notification..."
            className="pl-9 pr-9"
            data-testid="inbound-receipts-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#98A2B3] hover:text-[#344054]"
              data-testid="inbound-receipts-search-clear-btn"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>

        {activeTab === "completed" && (
          <div className="flex items-end gap-3 flex-wrap mb-5" data-testid="inbound-receipts-completed-filters">
            <div>
              <label className="text-xs font-medium text-[#667085] block mb-1">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="inbound-receipts-date-from" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#667085] block mb-1">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="inbound-receipts-date-to" />
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="inbound-receipts-date-clear-btn">
                Clear dates
              </Button>
            )}
          </div>
        )}

        {activeTab === "pending" && (
          loading ? (
            <div className="flex items-center justify-center py-24 text-[#667085]" data-testid="inbound-receipts-loading">
              <CircleNotch size={24} className="animate-spin mr-2" /> Loading pending receipts…
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-24 text-[#667085] bg-white rounded-xl border border-[#EAECF0]" data-testid="inbound-receipts-empty">
              Nothing pending — every shipped STO for this site has been received.
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-24 text-[#667085] bg-white rounded-xl border border-[#EAECF0]" data-testid="inbound-receipts-search-empty">
              No pending receipts match "{search}".
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#EAECF0] overflow-x-auto" data-testid="inbound-receipts-list">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>STO</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Ship To</TableHead>
                    <TableHead>Shipped On</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead className="min-w-[180px]">Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const status = rowStatus(order);
                    const rowBusy = !!order.active_job;
                    const isExpanded = expandedStoId === order.sto_id;
                    return (
                      <Fragment key={order.sto_id}>
                        <TableRow
                          data-testid={`inbound-receipt-row-${order.sto_id}`}
                          className="cursor-pointer"
                          onClick={() => setExpandedStoId(isExpanded ? null : order.sto_id)}
                        >
                          <TableCell>
                            <span className="text-xs font-semibold text-[#101828]">{order.sto_id}</span>
                            <span className="text-xs text-[#667085]"> · SAP #{order.sap_order_id}</span>
                            {order.outbound_delivery_ids.length > 0 && (
                              <div className="text-[11px] text-[#667085] font-mono mt-0.5 truncate" data-testid={`inbound-receipt-delivery-ids-${order.sto_id}`}>
                                SAP Delivery Notif: {order.outbound_delivery_ids.join(", ")}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1 text-xs text-[#344054] whitespace-nowrap">
                              {order.ship_from_site_id} <ArrowRight size={11} /> {order.ship_to_site_id}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-[#344054]">{order.ship_to_location_name || "—"}</TableCell>
                          <TableCell className="text-xs text-[#344054] whitespace-nowrap">{formatDate(order.created_at)}</TableCell>
                          <TableCell className="text-xs text-[#344054]">{order.items.length}</TableCell>
                          <TableCell>
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${status.cls}`} data-testid={`inbound-receipt-status-${order.sto_id}`}>
                              {status.label}
                            </span>
                            {order.receipt_error && (
                              <div className="text-[11px] text-[#B42318] mt-1 max-w-xs truncate" title={order.receipt_error}>{order.receipt_error}</div>
                            )}
                            {rowBusy && (
                              <div className="flex items-center gap-1 text-[11px] font-medium mt-1 text-[#0B6B74]" data-testid={`inbound-receipt-job-badge-${order.sto_id}`}>
                                <CircleNotch size={12} className="animate-spin" /> Receiving…
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" className="h-7 px-2.5 text-xs" onClick={(e) => { e.stopPropagation(); openReceive(order); }} disabled={rowBusy} data-testid={`inbound-receipt-receive-btn-${order.sto_id}`}>
                              {rowBusy ? <CircleNotch size={13} className="animate-spin mr-1" /> : null}
                              Receive
                            </Button>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className="bg-[#F8FAFC]" data-testid={`inbound-receipt-items-row-${order.sto_id}`}>
                            <TableCell colSpan={7} className="py-2">
                              <table className="w-full text-xs" data-testid={`inbound-receipt-items-table-${order.sto_id}`}>
                                <thead>
                                  <tr className="text-[#667085]">
                                    <th className="text-left py-1 font-medium">Product</th>
                                    <th className="text-left py-1 font-medium">Description</th>
                                    <th className="text-right py-1 font-medium">Qty</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(order.items || []).map((it) => (
                                    <tr key={it.line_no}>
                                      <td className="py-1 font-mono text-[#344054]">{it.product_id}</td>
                                      <td className="py-1 text-[#344054]">{it.description || "—"}</td>
                                      <td className="py-1 text-right text-[#344054]">{it.requested_qty != null ? Number(it.requested_qty).toLocaleString("en-IN", { maximumFractionDigits: 3 }) : "—"} {it.unit_of_measure}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )
        )}

        {activeTab === "completed" && (
          completedLoading ? (
            <div className="flex items-center justify-center py-24 text-[#667085]" data-testid="inbound-receipts-completed-loading">
              <CircleNotch size={24} className="animate-spin mr-2" /> Loading completed receipts…
            </div>
          ) : completedOrders.length === 0 ? (
            <div className="text-center py-24 text-[#667085] bg-white rounded-xl border border-[#EAECF0]" data-testid="inbound-receipts-completed-empty">
              No completed receipts found for this filter.
            </div>
          ) : filteredCompletedOrders.length === 0 ? (
            <div className="text-center py-24 text-[#667085] bg-white rounded-xl border border-[#EAECF0]" data-testid="inbound-receipts-completed-search-empty">
              No completed receipts match "{search}".
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#EAECF0] overflow-x-auto" data-testid="inbound-receipts-completed-list">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>STO</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Ship To</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Received At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Time Taken</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompletedOrders.map((order) => {
                    const status = COMPLETED_STATUS_STYLE[order.receipt_status] || COMPLETED_STATUS_STYLE.received;
                    return (
                      <TableRow key={order.sto_id} data-testid={`inbound-receipts-completed-row-${order.sto_id}`}>
                        <TableCell>
                          <span className="text-xs font-semibold text-[#101828]">{order.sto_id}</span>
                          <span className="text-xs text-[#667085]"> · SAP #{order.sap_order_id}</span>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-xs text-[#344054] whitespace-nowrap">
                            {order.ship_from_site_id} <ArrowRight size={11} /> {order.ship_to_site_id}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-[#344054]">{order.ship_to_location_name || "—"}</TableCell>
                        <TableCell className="text-xs font-mono text-[#344054] whitespace-nowrap" data-testid={`inbound-receipts-completed-delivery-${order.sto_id}`}>
                          {order.outbound_delivery_display_ids?.length > 0 ? order.outbound_delivery_display_ids.join(", ") : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-[#344054] whitespace-nowrap">{formatDate(order.received_at || order.receipt_completed_at)}</TableCell>
                        <TableCell>
                          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${status.cls}`} data-testid={`inbound-receipts-completed-status-${order.sto_id}`}>
                            {status.label}
                          </span>
                          {order.receipt_error && (
                            <div className="text-[11px] text-[#B42318] mt-1 max-w-xs truncate" title={order.receipt_error}>{order.receipt_error}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-[#344054] whitespace-nowrap" data-testid={`inbound-receipts-completed-duration-${order.sto_id}`}>
                          {order.receipt_duration_seconds != null
                            ? formatDuration(order.receipt_duration_seconds)
                            : order.manually_confirmed
                              ? <span className="text-[#667085] italic" title="Closed by SAP reconciliation, not started from this app - no automation time to show.">Reconciled</span>
                              : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => openView(order)} data-testid={`inbound-receipts-completed-view-btn-${order.sto_id}`}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )
        )}
      </div>

      {modal && (
        <ReceiptDetailModal order={modal.order} mode={modal.mode} onClose={closeModal} onSettled={handleSettled}
          isAdmin={user?.role === "super_admin" || user?.role === "admin"} />
      )}
    </div>
  );
}
